import React, { ReactNode, useEffect, cloneElement, ReactElement, SetStateAction, Dispatch } from 'react';
import {
  offset,
  shift,
  flip,
  hide as hideMiddleware,
  autoUpdate,
  useFloating,
  useInteractions,
  useRole,
  useDismiss,
  useClick,
  FloatingPortal,
  FloatingFocusManager,
} from '@floating-ui/react';
import { getZIndex } from '../../styles';
import { FLOATING_ROOT_ID } from '../../constants';
import classNames from 'classnames';

type ContentWithCloseAction = (action: { close(): void }) => ReactNode;

export interface DayPickerOverlayProps {
  /** @desc reference element를 구할 수 있는 컴포넌트여야 합니다. */
  children: React.ReactNode;
  className?: string;
  content?: ReactNode | ContentWithCloseAction;
  /** @default getZIndex('picker') */
  zIndex?: number;
  opened?: boolean;
  disabled?: boolean;
  onClose?: () => void;
  onOpen?: () => void;
  onCloseOutsidePress?: () => void;
  setOpened: Dispatch<SetStateAction<boolean>>;
}

export const DayPickerOverlay = ({
  children,
  className,
  content,
  zIndex = getZIndex('picker'),
  opened: openProp,
  disabled,
  onClose,
  onOpen,
  onCloseOutsidePress,
  setOpened,
}: DayPickerOverlayProps) => {
  const handleClose = () => {
    setOpened(false);
    onClose?.();
  };

  const handleOpen = (open: boolean) => {
    setOpened(open);
    !open && onCloseOutsidePress?.();
  };

  const {
    refs,
    floatingStyles,
    context,
    middlewareData: { hide },
  } = useFloating({
    open: openProp,
    onOpenChange: handleOpen,
    placement: 'bottom-start',
    whileElementsMounted: autoUpdate,
    middleware: [offset(4), shift(), flip(), hideMiddleware()],
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useClick(context, { keyboardHandlers: false, enabled: !disabled }),
    useRole(context, { role: 'dialog' }),
    useDismiss(context),
  ]);

  useEffect(() => {
    openProp ? onOpen?.() : onClose?.();
  }, [openProp]);

  return (
    <>
      {cloneElement(
        children as ReactElement,
        getReferenceProps({ ref: refs.setReference, ...(children as ReactElement).props }),
      )}
      {openProp && (
        <FloatingPortal id={FLOATING_ROOT_ID}>
          <FloatingFocusManager context={context} initialFocus={-1}>
            <div
              {...getFloatingProps({
                className: classNames('pds-date-picker-overlay', className),
                ref: refs.setFloating,
                style: {
                  ...floatingStyles,
                  zIndex,
                  display: hide?.referenceHidden ? 'none' : 'block',
                },
              })}
            >
              {typeof content === 'function' ? content({ close: handleClose }) : content}
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  );
};
